import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button } from '@mui/material';

import { ErrorOverlayContainer, ErrorOverlayText } from './GeneratikTable.styles';

export interface GeneratikTableErrorOverlayProps extends HTMLAttributes<HTMLDivElement> {
  message?: string;
  onRetry?: () => void;
}

/**
 * Rendered in place of the no-rows overlay when the query backing the table
 * failed. Without it a 500/404/network failure is indistinguishable from an
 * empty result set and users read absent data as authoritative.
 */
const GeneratikTableErrorOverlay = ({
  message,
  onRetry,
  ...rest
}: GeneratikTableErrorOverlayProps) => {
  const { t } = useTranslation();

  return (
    <ErrorOverlayContainer {...rest} data-testid="table-error-state">
      <ErrorOutlineIcon fontSize="medium" />
      <ErrorOverlayText variant="body1">
        {message || t('errors.something_went_wrong')}
      </ErrorOverlayText>
      {onRetry && (
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          data-testid="table-error-retry"
        >
          {t('error_fallback_retry')}
        </Button>
      )}
    </ErrorOverlayContainer>
  );
};

export default GeneratikTableErrorOverlay;
